import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileCheck2 } from "lucide-react";
import { setPendingUpload } from "@/lib/pendingUpload";

// iter-61 — Interactive drag-and-drop hero widget. Follows the operator's
// spec: dashed perimeter, hover animation (scale + border), 📥 iconography,
// upload microcopy. On drop OR click it routes the user to /register with
// the intent captured so the auth screen can prefill the "start with an
// upload" affordance. No credit card required — first 3 chapters preview.

export default function HeroDropZone({ onFile }) {
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [fileName, setFileName] = useState("");

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const routeToUpload = useCallback(
    (file) => {
      // iter-62 — stash the actual File object in a module-level ref
      // (see lib/pendingUpload.js) so /register can auto-continue into
      // an upload post-signup. sessionStorage holds the filename for
      // hard-reload friendliness.
      setPendingUpload(file || null);
      navigate("/register");
    },
    [navigate]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) {
        setFileName(file.name);
        onFile?.(file);
        routeToUpload(file);
      }
    },
    [onFile, routeToUpload]
  );

  const handlePick = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) {
        setFileName(file.name);
        onFile?.(file);
        routeToUpload(file);
      }
    },
    [onFile, routeToUpload]
  );

  return (
    <div
      className={`relative rounded-lg border-2 border-dashed bg-white transition-all duration-300 transform hover:scale-[1.01] ${
        dragActive
          ? "border-[color:var(--primary)] bg-[#fff7f2] scale-[1.02]"
          : "border-[color:var(--border)]"
      }`}
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      data-testid="hero-dropzone"
    >
      <label
        htmlFor="hero-file-input"
        className="flex flex-col items-center justify-center px-8 py-12 cursor-pointer"
        data-testid="hero-dropzone-label"
      >
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center mb-5 transition-colors ${
            dragActive
              ? "bg-[color:var(--primary)] text-white"
              : "bg-[#f2f1ec] text-[color:var(--text)]"
          }`}
          aria-hidden
        >
          {fileName ? (
            <FileCheck2 className="w-6 h-6" strokeWidth={1.5} />
          ) : (
            <Upload className="w-6 h-6" strokeWidth={1.5} />
          )}
        </div>
        <p className="font-editorial text-2xl text-center leading-snug">
          {fileName ? (
            <>Ready with <span className="italic text-[color:var(--primary)]">{fileName}</span></>
          ) : (
            <>
              Drag &amp; drop your raw{" "}
              <span className="italic">.docx</span>,{" "}
              <span className="italic">.md</span>, or{" "}
              <span className="italic">.txt</span> file
            </>
          )}
        </p>
        <p className="mt-3 text-sm text-[color:var(--text-muted)] text-center">
          No credit card required · Instantly preview your first 3 chapters
          formatted for free
        </p>
        <span className="mt-6 btn-primary inline-flex items-center gap-2 pointer-events-none">
          Choose file
        </span>
        <input
          id="hero-file-input"
          type="file"
          className="sr-only"
          accept=".docx,.md,.txt,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
          onChange={handlePick}
          data-testid="hero-file-input"
        />
      </label>
    </div>
  );
}
